import { useState } from "react";

import { PasswordField } from "@/components/auth/PasswordField";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";


export const ProtectedDeleteDialog = ({ triggerLabel, title, description, onConfirm, testIdPrefix, triggerDisabled = false }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep(1);
    setPassword("");
    setError("");
    setLoading(false);
  };

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) reset();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      await onConfirm(password);
      handleOpenChange(false);
    } catch (message) {
      setError(message || "We could not complete the hard delete.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-full border-rose-200 text-rose-700 hover:bg-rose-50" disabled={triggerDisabled} data-testid={`${testIdPrefix}-trigger-button`}>{triggerLabel}</Button>
      </DialogTrigger>
      <DialogContent className="border-stone-200 bg-white" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle data-testid={`${testIdPrefix}-title`}>{title}</DialogTitle>
          <DialogDescription data-testid={`${testIdPrefix}-description`}>{description}</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4" data-testid={`${testIdPrefix}-step-one`}>
            <p className="rounded-[1rem] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid={`${testIdPrefix}-first-warning-text`}>Are you sure you want to take this action?</p>
            <DialogFooter>
              <Button variant="outline" className="rounded-full" onClick={() => handleOpenChange(false)} data-testid={`${testIdPrefix}-cancel-button`}>Cancel</Button>
              <Button className="rounded-full" style={{ backgroundColor: "var(--brand-primary-dark, #2B2B2B)", color: "var(--brand-white, #FFFFFF)" }} onClick={() => setStep(2)} data-testid={`${testIdPrefix}-continue-button`}>Continue</Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid={`${testIdPrefix}-step-two`}>
            <p className="text-sm text-slate-700">Re-enter the current super admin password to continue.</p>
            <PasswordField value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Super admin password" data-testid={`${testIdPrefix}-password-input`} />
            <DialogFooter>
              <Button variant="outline" className="rounded-full" onClick={() => setStep(1)} data-testid={`${testIdPrefix}-back-button`}>Back</Button>
              <Button className="rounded-full" style={{ backgroundColor: "var(--brand-primary-dark, #2B2B2B)", color: "var(--brand-white, #FFFFFF)" }} onClick={() => setStep(3)} disabled={!password} data-testid={`${testIdPrefix}-password-confirm-button`}>Continue</Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid={`${testIdPrefix}-step-three`}>
            <p className="rounded-[1rem] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid={`${testIdPrefix}-final-warning-text`}>This action cannot be reversed.</p>
            {error && <p className="text-sm text-rose-700" data-testid={`${testIdPrefix}-error-text`}>{error}</p>}
            <DialogFooter>
              <Button variant="outline" className="rounded-full" onClick={() => setStep(2)} data-testid={`${testIdPrefix}-final-back-button`}>Back</Button>
              <Button className="rounded-full bg-rose-600 text-white hover:bg-rose-700" onClick={handleSubmit} disabled={loading} data-testid={`${testIdPrefix}-final-confirm-button`}>{loading ? "Deleting..." : "Hard delete permanently"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
